import {
  forwardRef,
  useCallback,
  type MouseEvent,
  type KeyboardEvent,
} from "react";
import {
  Link,
  createPath,
  useLocation,
  useNavigate,
  type LinkProps,
  type To,
} from "react-router-dom";
import { matchContext } from "./contexts";
import { isPlanningPath } from "./mainNav";
import { useInPanel, useOptionalPanel } from "./panel";
import { splitHref } from "./paths";

/** Where a link opens on a plain click. */
export type OpenIn = "main" | "panel";

/** Routes that only exist in the side panel (C6). */
const PANEL_ONLY_PATTERNS = [/^\/tasks\/[^/]+/, /^\/signal-trace\//];

export function isPanelOnlyPath(pathname: string) {
  return PANEL_ONLY_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * K3: decides between main view and side panel from where the link is
 * (`fromPathname`, the main view) and what it points to.
 *
 * - panel-only records (tasks) always open in the panel;
 * - planning pages open entities in the panel;
 * - inside an entity, links to a different entity open in the panel, links
 *   within the same context (its sections and child pages) stay in main;
 * - lists, overviews, tools and navigation open in the main view.
 */
export function decideOpenIn(fromPathname: string, targetHref: string): OpenIn {
  const { pathname: target } = splitHref(targetHref);
  if (isPanelOnlyPath(target)) return "panel";
  const targetContext = matchContext(target);
  if (!targetContext) return "main";
  if (isPlanningPath(fromPathname)) return "panel";
  const fromContext = matchContext(fromPathname);
  if (!fromContext || fromContext.key === targetContext.key) return "main";
  // An entity's own child with its own context, e.g. studio → config.
  if (targetContext.def.ownerKey?.(targetContext.params) === fromContext.key) {
    return "main";
  }
  // …and back from the child to its owner.
  if (fromContext.def.ownerKey?.(fromContext.params) === targetContext.key) {
    return "main";
  }
  return "panel";
}

function hrefOf(to: To) {
  return typeof to === "string" ? to : createPath(to);
}

export function isPlainLeftClick(event: MouseEvent | KeyboardEvent) {
  if ("button" in event && event.button !== 0) return false;
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * Programmatic counterpart of `AppLink`: opens `href` where K3 says, or as
 * requested by `openIn`. Use for rows/cells that cannot be anchors.
 * Prefer `AppLink` wherever possible (K1).
 */
export function useOpenLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const panel = useOptionalPanel();
  const inPanel = useInPanel();

  return useCallback(
    (
      href: string,
      options: {
        openIn?: OpenIn;
        event?: MouseEvent | KeyboardEvent;
        replace?: boolean;
      } = {},
    ) => {
      // K2: modifier clicks always open a full page in a new tab.
      if (options.event && !isPlainLeftClick(options.event)) {
        window.open(href, "_blank", "noopener");
        return;
      }
      // Inside the panel, the panel's own navigator keeps it in the panel.
      if (inPanel || !panel) {
        navigate(href, { replace: options.replace });
        return;
      }
      const openIn = options.openIn ?? decideOpenIn(location.pathname, href);
      if (openIn === "panel") panel.open(href, { replace: options.replace });
      else navigate(href, { replace: options.replace });
    },
    [inPanel, location.pathname, navigate, panel],
  );
}

export interface AppLinkProps extends LinkProps {
  /** Overrides the K3 decision for a plain click. */
  openIn?: OpenIn;
}

/**
 * The one link component for navigation targets (K1, K4). Renders a real
 * anchor so cmd/ctrl/shift/middle-click and long-press open a new tab with
 * the full page (K2); a plain click opens the main view or the side panel
 * following K3.
 */
export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(
  function AppLink({ to, openIn, onClick, target, ...props }, ref) {
    const location = useLocation();
    const panel = useOptionalPanel();
    const inPanel = useInPanel();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || target || inPanel || !panel) return;
      if (!isPlainLeftClick(event)) return;
      const href = hrefOf(to);
      const decision = openIn ?? decideOpenIn(location.pathname, href);
      if (decision === "panel") {
        event.preventDefault();
        panel.open(href);
      }
    };

    return (
      <Link
        ref={ref}
        to={to}
        target={target}
        onClick={handleClick}
        {...props}
      />
    );
  },
);
